import React, { useState, useEffect, useCallback } from 'react';
import { metricsService } from '../services/metricsService';
import PlatformKpis from './metrics/PlatformKpis';
import PlatformChart, { MEASURES } from './metrics/PlatformChart';
import ServicesPanel from './metrics/ServicesPanel';
import UsersTable from './metrics/UsersTable';
import { AUDIENCES, audienceParam, toggleAudience } from './metrics/audience';

/**
 * Метрики платформы — вкладка Админки. Состав и верстка по артборду
 * «Метрики платформы» (docs/redesign/2026-09-04-bumaga/Newton - Админка.dc.html):
 * период строкой сверху, шесть показателей, слева графики (минуты, задачи,
 * люди, новички, встречи с ботом) столбиком, справа сервисы, внизу таблица людей. Шаг ряда выбирается
 * отдельно от периода (12.09.2026, просьба владельца), но не любой — см. grainsFor.
 *
 * Фильтр «Кто» (сотрудники, клиенты, партнёры, служебные учётки — одна группа,
 * несколько или все, решение владельца 14.10.2026) сужает всю вкладку; столбики
 * графиков складываются из тех же групп. Группу по роли учётки ставит bit-metrics.
 *
 * 🪤 Чего из артборда здесь нет — и это упирается в bit-metrics, а не в вёрстку:
 *  — поиск по людям: у `GET /users` нет параметра запроса, а фильтровать
 *    страницу из двадцати — врать при пагинации;
 *  — «Сейчас на платформе»: очередь своя у каждого ASR-сервиса, общего
 *    агрегата нет.
 * Оба записаны в docs/backlog/redesign-shell-gaps.md.
 */

const PAGE_SIZE = 20;

const HOUR = 3600e3;
const DAY = 24 * HOUR;

// Период → сколько это миллисекунд и какой шаг ряда по умолчанию.
const PERIODS = [
  ['1h', '1 час', HOUR, 'hour'],
  ['24h', '24 часа', DAY, 'hour'],
  ['7d', '7 дней', 7 * DAY, 'day'],
  ['30d', '30 дней', 30 * DAY, 'day'],
  ['90d', '90 дней', 90 * DAY, 'week'],
];

const GRAINS = [['hour', 'Час'], ['day', 'День'], ['week', 'Неделя']];

/**
 * Какие шаги ряда допустимы для периода. Часовой ряд длиннее недели — это
 * тысячи столбиков и полный пересчёт по событиям у bit-metrics (за 90 дней —
 * 14 секунд: срезы у него дневные, час из них не собрать); дневной короче
 * недели и недельный короче двух недель — один-два столбика вместо графика.
 */
export function grainsFor(span) {
  const grains = [];
  if (span <= 7 * DAY) grains.push('hour');
  if (span >= 7 * DAY) grains.push('day');
  if (span >= 14 * DAY) grains.push('week');
  return grains;
}

/** Выбранный шаг остаётся при смене периода, пока допустим; иначе — умолчание периода. */
export function pickGrain(current, span) {
  if (grainsFor(span).includes(current)) return current;
  const found = PERIODS.find(([, , ms]) => ms === span);
  return found ? found[3] : grainsFor(span)[0];
}

const MetricsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);          // текущий период
  const [seriesPrev, setSeriesPrev] = useState(null);  // тот же период назад — для дельт
  const [protocols, setProtocols] = useState({ now: 0, before: 0 });
  const [usersData, setUsersData] = useState(null);

  const [loadingTop, setLoadingTop] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);

  const [period, setPeriod] = useState('30d');
  const [granularity, setGranularity] = useState('day');
  const [sortBy, setSortBy] = useState('audio_seconds');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [audience, setAudience] = useState([]);   // пусто — «Все»

  const spec = PERIODS.find(([id]) => id === period) || PERIODS[3];
  const span = spec[2];

  const loadTop = useCallback(async () => {
    setLoadingTop(true);
    const now = Date.now();
    const from = new Date(now - span).toISOString();
    const prevFrom = new Date(now - span * 2).toISOString();
    const who = audienceParam(audience);
    const base = { granularity, date_from: from };
    if (selectedService) base.service = selectedService;
    if (who) base.audience = who;
    const proto = { granularity, service: 'bit-llm-summarize' };
    if (who) proto.audience = who;
    try {
      const [ov, cur, prev, protoNow, protoPrev] = await Promise.all([
        metricsService.getOverview(who ? { audience: who } : {}),
        metricsService.getTimeseries(base),
        metricsService.getTimeseries({ ...base, date_from: prevFrom, date_to: from }),
        metricsService.getTimeseries({ ...proto, date_from: from }),
        metricsService.getTimeseries({ ...proto, date_from: prevFrom, date_to: from }),
      ]);
      setOverview(ov);
      setSeries(cur?.buckets || []);
      setSeriesPrev(prev?.buckets || []);
      const sum = (d) => (d?.buckets || []).reduce((acc, b) => acc + (b.tasks || 0), 0);
      setProtocols({ now: sum(protoNow), before: sum(protoPrev) });
      setError(null);
    } catch (e) {
      setError('Не удалось загрузить метрики платформы');
    } finally {
      setLoadingTop(false);
    }
  }, [granularity, span, selectedService, audience]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        page_size: PAGE_SIZE,
        date_from: new Date(Date.now() - span).toISOString(),
      };
      if (selectedService) params.service = selectedService;
      const who = audienceParam(audience);
      if (who) params.audience = who;
      setUsersData(await metricsService.getUsers(params));
    } catch (e) {
      setError('Не удалось загрузить список пользователей');
    } finally {
      setUsersLoading(false);
    }
  }, [sortBy, sortOrder, page, span, selectedService, audience]);

  useEffect(() => { loadTop(); }, [loadTop]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSelectService = (svc) => {
    setSelectedService((cur) => (cur === svc ? null : svc));
    setPage(1);
  };

  const pickAudience = (id) => {
    // «Все» при пустом выборе — тот же массив: без него вкладка грузилась бы заново.
    setAudience((cur) => (id === null ? (cur.length ? [] : cur) : toggleAudience(cur, id)));
    setPage(1);
  };

  const handleSort = (field) => {
    if (field === sortBy) setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(field); setSortOrder('desc'); }
    setPage(1);
  };

  const pickPeriod = (id) => {
    const found = PERIODS.find(([key]) => key === id);
    setPeriod(id);
    setPage(1);
    if (found) setGranularity((g) => pickGrain(g, found[2]));
  };

  const totalPages = usersData ? Math.ceil(usersData.total_count / PAGE_SIZE) : 0;
  const grainTitle = granularity === 'hour' ? 'часам' : granularity === 'week' ? 'неделям' : 'дням';
  const allowedGrains = grainsFor(span);

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <div className="rounded-panel bg-paper-accent/[0.06] px-4 py-3 text-[13px] text-paper-accent">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 px-[6px]">
        <div className="flex gap-1">
          {PERIODS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => pickPeriod(id)}
                    className={`rounded-chip px-3 py-[6px] text-[13px] font-medium transition-colors ${
                      period === id ? 'bg-paper-ink text-white'
                                    : 'bg-paper-card text-paper-ink-2 hover:bg-paper-panel'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-paper-ink-3">Шаг</span>
          <div className="flex gap-1">
            {GRAINS.map(([id, label]) => {
              const allowed = allowedGrains.includes(id);
              return (
                <button key={id} type="button" disabled={!allowed}
                        onClick={() => setGranularity(id)}
                        title={allowed ? '' : 'Недоступно для этого периода'}
                        className={`rounded-chip px-[10px] py-1 text-[12px] font-medium transition-colors ${
                          granularity === id ? 'bg-paper-ink text-white'
                            : allowed ? 'bg-paper-card text-paper-ink-2 hover:bg-paper-panel'
                              : 'cursor-not-allowed bg-paper-card text-paper-hint'}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-paper-ink-3">Кто</span>
          <div className="flex flex-wrap gap-1">
            <button type="button" onClick={() => pickAudience(null)} aria-pressed={audience.length === 0}
                    className={`rounded-chip px-3 py-[6px] text-[13px] font-medium transition-colors ${
                      audience.length === 0 ? 'bg-paper-ink text-white'
                                            : 'bg-paper-card text-paper-ink-2 hover:bg-paper-panel'}`}>
              Все
            </button>
            {AUDIENCES.map(([id, label, color]) => {
              const on = audience.includes(id);
              return (
                <button key={id} type="button" onClick={() => pickAudience(id)} aria-pressed={on}
                        className={`inline-flex items-center gap-[6px] rounded-chip px-3 py-[6px] text-[13px] font-medium transition-colors ${
                          on ? 'bg-paper-ink text-white'
                             : 'bg-paper-card text-paper-ink-2 hover:bg-paper-panel'}`}>
                  {/* Точка — цвет группы в столбиках графиков: кнопки и есть легенда. */}
                  <span className="h-2 w-2 shrink-0 rounded-full"
                        style={{ background: color, boxShadow: on ? '0 0 0 1px #fff' : 'none' }} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {loadingTop ? (
        <div className="px-[6px] text-[13px] text-paper-ink-3">Загружаем показатели…</div>
      ) : (
        <PlatformKpis
          current={series}
          previous={seriesPrev}
          protocols={protocols.now}
          protocolsPrev={protocols.before}
          service={selectedService}
        />
      )}

      <div className="grid gap-3 grid-cols-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-3">
          {MEASURES.map(([id, label, , , platformWide]) => (
            <PlatformChart
              key={id}
              buckets={series}
              granularity={granularity}
              measure={id}
              title={`${label} по ${grainTitle}${selectedService && !platformWide ? ` · ${selectedService}` : ''}`}
            />
          ))}
        </div>
        <ServicesPanel
          data={overview}
          selectedService={selectedService}
          onSelectService={handleSelectService}
        />
      </div>

      <section className="flex flex-col gap-4 rounded-card bg-paper-card px-6 py-[22px]">
        <div className="flex items-baseline gap-2">
          <span className="text-[12px] text-paper-ink-3">Пользователи</span>
          {usersData && (
            <span className="font-mono text-[12px] text-paper-hint">
              {usersData.total_count} за период{selectedService ? ` · ${selectedService}` : ''}
            </span>
          )}
        </div>
        <UsersTable
          data={usersData?.users}
          loading={usersLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          page={page}
          totalPages={totalPages}
          onPage={setPage}
        />
      </section>
    </div>
  );
};

export default MetricsDashboard;
